import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-2" }
const _hoisted_2 = { class: "flex justify-between items-center text-xs" }

import { ref, onMounted, onBeforeUnmount, watch } from "vue";

	
export default /*#__PURE__*/_defineComponent({
  __name: 'SignaturePad',
  props: {
    modelValue: {},
    width: {},
    height: {}
  },
  emits: ["update:modelValue", "empty-changed"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

	/**
	 * Touch + mouse signature canvas. Emits the captured signature as
	 * a `data:image/png;base64,...` string via v-model so the parent
	 * can POST it directly to the delivery-proof endpoint.
	 */
	const props = __props;
	const emit = __emit;

	const canvas = ref<HTMLCanvasElement | null>(null);
	const isDrawing = ref(false);
	const isEmpty = ref(true);
	let ctx: CanvasRenderingContext2D | null = null;
	let lastPoint: { x: number; y: number } | null = null;

	const w = props.width || 600;
	const h = props.height || 200;

	const setup = () => {
		if (!canvas.value) return;
		const dpr = window.devicePixelRatio || 1;
		canvas.value.width = w * dpr;
		canvas.value.height = h * dpr;
		canvas.value.style.width = w + "px";
		canvas.value.style.height = h + "px";
		ctx = canvas.value.getContext("2d");
		if (!ctx) return;
		ctx.scale(dpr, dpr);
		ctx.fillStyle = "#ffffff";
		ctx.fillRect(0, 0, w, h);
		ctx.lineWidth = 2;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";
		ctx.strokeStyle = "#111827";

		// Restore an existing signature when the parent passes one in
		// (eg. modal reopened with the previously captured data URL).
		if (props.modelValue) {
			loadFromDataUrl(props.modelValue);
		}
	};

	/**
	 * Paint a signature data URL onto the canvas. Used both at mount
	 * (restore previous signature) and via the watcher when the parent
	 * sets `modelValue` programmatically.
	 */
	const loadFromDataUrl = (dataUrl: string) => {
		if (!ctx || !canvas.value || !dataUrl) return;
		const img = new Image();
		img.onload = () => {
			if (!ctx) return;
			ctx.fillStyle = "#ffffff";
			ctx.fillRect(0, 0, w, h);
			ctx.drawImage(img, 0, 0, w, h);
			isEmpty.value = false;
			emit("empty-changed", false);
		};
		img.onerror = () => {
			console.warn("[SignaturePad] failed to restore signature image");
		};
		img.src = dataUrl;
	};

	const getPoint = (e: MouseEvent | TouchEvent): { x: number; y: number } => {
		if (!canvas.value) return { x: 0, y: 0 };
		const rect = canvas.value.getBoundingClientRect();
		const t = (e as TouchEvent).touches ? (e as TouchEvent).touches[0] : (e as MouseEvent);
		return { x: t.clientX - rect.left, y: t.clientY - rect.top };
	};

	const start = (e: MouseEvent | TouchEvent) => {
		e.preventDefault();
		isDrawing.value = true;
		lastPoint = getPoint(e);
	};

	const draw = (e: MouseEvent | TouchEvent) => {
		if (!isDrawing.value || !ctx || !lastPoint) return;
		e.preventDefault();
		const p = getPoint(e);
		ctx.beginPath();
		ctx.moveTo(lastPoint.x, lastPoint.y);
		ctx.lineTo(p.x, p.y);
		ctx.stroke();
		lastPoint = p;
		if (isEmpty.value) {
			isEmpty.value = false;
			emit("empty-changed", false);
		}
	};

	const end = () => {
		if (!isDrawing.value) return;
		isDrawing.value = false;
		lastPoint = null;
		if (canvas.value && !isEmpty.value) {
			emit("update:modelValue", canvas.value.toDataURL("image/png"));
		}
	};

	const clear = () => {
		if (!ctx || !canvas.value) return;
		ctx.fillStyle = "#ffffff";
		ctx.fillRect(0, 0, w, h);
		isEmpty.value = true;
		emit("empty-changed", true);
		emit("update:modelValue", "");
	};

	__expose({ clear, isEmpty: () => isEmpty.value });

	onMounted(() => {
		setup();
		window.addEventListener("mouseup", end);
		window.addEventListener("touchend", end);
	});

	onBeforeUnmount(() => {
		window.removeEventListener("mouseup", end);
		window.removeEventListener("touchend", end);
	});

	watch(() => props.modelValue, (v) => {
		if (!v && !isEmpty.value) {
			clear();
			return;
		}
		// Parent set a new (non-empty) value programmatically and the
		// canvas is currently blank — paint it back. Skip when we're
		// already drawing (the user is mid-stroke and we just emitted
		// the data URL ourselves on `end()`), otherwise we'd flicker.
		if (v && isEmpty.value && !isDrawing.value) {
			loadFromDataUrl(v);
		}
	});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("canvas", {
      ref_key: "canvas",
      ref: canvas,
      class: "border border-gray-400 rounded bg-white cursor-crosshair touch-none",
      onMousedown: start,
      onMousemove: draw,
      onTouchstart: start,
      onTouchmove: draw
    }, null, 544),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("span", {
        class: _normalizeClass(isEmpty.value ? 'text-gray-400' : 'text-green-500')
      }, _toDisplayString(isEmpty.value ? 'Signez ci-dessus' : 'Signature capturée'), 3),
      _createElementVNode("button", {
        type: "button",
        class: "text-accent hover:underline",
        onClick: clear
      }, "Effacer")
    ])
  ]))
}
}

})